import { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, Link, useSearchParams } from 'react-router';

import { AppRoutes, THROTTLE_LIMIT } from '@/constants';
import { useDebounce, useThrottle } from '@/hooks';
import { navigate } from '@/providers';
import { SortDirection } from '@/types';
import { formatDate } from '@/utils';

import { CloseIcon } from '@/assets/icons';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IDateRange,
  Loading,
  MultiSelect,
  NotFoundResult,
  Pagination,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common';

import { CHECK_IN_STATUS_OPTIONS, PROFILE_TYPE_OPTIONS } from '@/features/check-in-logs/constants';
import { CheckInStatus, ProfileType } from '@/features/check-in-logs/enums';
import { useCheckInLogsSearching } from '@/features/check-in-logs/hooks';
import { ICheckInLogSearchingPayload } from '@/features/check-in-logs/interfaces';
import {
  getCheckInStatusIcon,
  getCheckInStatusLabel,
  getCheckInStatusVariant,
  getProfileTypeLabel,
  mapSerialNumberResponseToDropdownSearchResponse,
} from '@/features/check-in-logs/utils';
import { DeviceType } from '@/features/ipad-configs/enums';
import { IpadConfigService } from '@/features/ipad-configs/services';
import { DateRangePicker } from './DateRangePicker';

const CheckInLogList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get('page'));
  const initialPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [pageSize] = useState<number>(10);
  const [profileTypes, setProfileTypes] = useState<string[]>(
    searchParams.get('types')?.split(',').filter(Boolean) || [],
  );
  const [rooms, setRooms] = useState<string[]>(
    searchParams.get('rooms')?.split(',').filter(Boolean) || [],
  );
  const [statuses, setStatuses] = useState<string[]>(
    searchParams.get('statuses')?.split(',').filter(Boolean) || [],
  );
  const [timestamp, setTimestamp] = useState<IDateRange>({
    from: searchParams.get('from') ? new Date(searchParams.get('from') as string) : undefined,
    to: searchParams.get('to') ? new Date(searchParams.get('to') as string) : undefined,
  });
  const [sortBy, setSortBy] = useState<string>(searchParams.get('sortBy') || 'timestamp');
  const [isDescending, setIsDescending] = useState<boolean>(searchParams.get('desc') !== 'false');
  const dateRangePickerRef = useRef<any>(null);
  const previousFilterKeyRef = useRef<string | null>(null);
  const maxPastDays = Number(process.env.REACT_APP_CHECK_IN_LOG_MAX_PAST_DAYS) || 365;

  const minSelectableDate = useMemo(
    () => new Date(Date.now() - maxPastDays * 24 * 60 * 60 * 1000),
    [maxPastDays],
  );

  // Handle column sorting
  const handleSort = (columnKey: string, direction: SortDirection) => {
    if (direction === null) {
      setSortBy('');
      setIsDescending(true);
    } else {
      setSortBy(columnKey);
      setIsDescending(direction === 'desc');
    }
  };

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery);

  // Throttle filters and sorting
  const throttledProfileTypes = useThrottle(profileTypes, THROTTLE_LIMIT, true);
  const throttledRooms = useThrottle(rooms, THROTTLE_LIMIT, true);
  const throttledStatus = useThrottle(statuses, THROTTLE_LIMIT, true);
  const throttledTimestamp = useThrottle(timestamp, THROTTLE_LIMIT, true);
  const throttledSortBy = useThrottle(sortBy, THROTTLE_LIMIT, true);
  const throttledIsDescending = useThrottle(isDescending, THROTTLE_LIMIT, true);

  // Build API payload
  const apiPayload = useMemo(() => {
    const payload: ICheckInLogSearchingPayload = {
      page: currentPage,
      isDescending: throttledIsDescending ?? true,
    };

    if (debouncedSearchQuery) {
      payload.searchBy = debouncedSearchQuery;
    }

    if (throttledProfileTypes && throttledProfileTypes.length === 1) {
      payload.profileType = Number(throttledProfileTypes[0]) as ProfileType;
    }

    if (throttledRooms && throttledRooms.length > 0) {
      payload.serialNumbers = throttledRooms;
    }

    const statusCount = Object.keys(CheckInStatus).length / 2; // Divide by 2 because of how enums are compiled in TS

    if (throttledStatus && throttledStatus.length > 0 && throttledStatus.length < statusCount) {
      payload.statuses = throttledStatus.map((status) => Number(status) as CheckInStatus);
    }

    if (throttledSortBy) {
      payload.sortBy = throttledSortBy;
    }

    if (throttledTimestamp?.from && throttledTimestamp?.to) {
      payload.from = throttledTimestamp.from;
      payload.to = throttledTimestamp.to;
    }

    return payload;
  }, [
    debouncedSearchQuery,
    currentPage,
    throttledProfileTypes,
    throttledRooms,
    throttledStatus,
    throttledTimestamp,
    throttledSortBy,
    throttledIsDescending,
  ]);

  // Fetch data from API
  const { data, isLoading, isFetching, isError } = useCheckInLogsSearching(apiPayload);

  const checkInLogs = useMemo(() => data?.data || [], [data?.data]);
  const totalPages = useMemo(() => data?.metadata.totalPages || 0, [data?.metadata.totalPages]);
  const totalItems = useMemo(() => data?.metadata.totalItems || 0, [data?.metadata.totalItems]);

  const isFiltered = useMemo(
    () =>
      searchQuery.length > 0 ||
      profileTypes.length > 0 ||
      rooms.length > 0 ||
      statuses.length > 0 ||
      (timestamp.from !== undefined && timestamp.to !== undefined),
    [searchQuery, profileTypes, rooms, statuses, timestamp],
  );

  const isEmpty = useMemo(() => checkInLogs.length === 0, [checkInLogs]);

  const onClearFilters = () => {
    setSearchQuery('');
    setProfileTypes([]);
    setRooms([]);
    setStatuses([]);
    setTimestamp({ from: undefined, to: undefined });
    dateRangePickerRef.current?.onClear();
  };

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (currentPage > 1) params.set('page', String(currentPage));
    if (searchQuery) params.set('search', searchQuery);
    if (profileTypes.length) params.set('types', profileTypes.join(','));
    if (rooms.length) params.set('rooms', rooms.join(','));
    if (statuses.length) params.set('statuses', statuses.join(','));
    if (timestamp.from) params.set('from', timestamp.from.toISOString());
    if (timestamp.to) params.set('to', timestamp.to.toISOString());
    if (sortBy !== 'timestamp') params.set('sortBy', sortBy);
    if (!isDescending) params.set('desc', 'false');

    setSearchParams(params, { replace: true });
  }, [
    searchQuery,
    profileTypes,
    rooms,
    statuses,
    timestamp,
    sortBy,
    isDescending,
    setSearchParams,
    currentPage,
  ]);

  // Reset to page 1 when filters actually change (skip initial mount + throttle settling)
  useEffect(() => {
    const filterKey = JSON.stringify([
      debouncedSearchQuery,
      throttledProfileTypes,
      throttledRooms,
      throttledStatus,
      throttledTimestamp?.from?.getTime() ?? null,
      throttledTimestamp?.to?.getTime() ?? null,
      throttledSortBy,
      throttledIsDescending,
    ]);

    if (previousFilterKeyRef.current === null) {
      previousFilterKeyRef.current = filterKey;
      return;
    }

    if (previousFilterKeyRef.current !== filterKey) {
      previousFilterKeyRef.current = filterKey;
      setCurrentPage(1);
    }
  }, [
    debouncedSearchQuery,
    throttledProfileTypes,
    throttledRooms,
    throttledStatus,
    throttledTimestamp,
    throttledSortBy,
    throttledIsDescending,
  ]);

  if (isError) {
    navigate(AppRoutes.INTERNAL_SERVER_ERROR);
  }

  return (
    <div className="w-full flex flex-col items-start justify-start">
      <h1 className="font-bold text-[32px] text-neutral-n10">Check-in Logs</h1>
      <div className="flex flex-row items-start justify-between w-full mt-8 mb-5">
        <div className="flex flex-row flex-wrap items-start justify-start gap-2">
          <Search
            placeholder="Search by Full Name, Face Profile ID or Employee ID"
            className="gap-2 !h-11 !w-[330px] sm:!w-[390px]"
            style={{ height: '44px', width: '390px' }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <MultiSelect
            options={PROFILE_TYPE_OPTIONS}
            defaultValue={profileTypes}
            placeholder="Profile Type"
            onValueChange={(values) => {
              setProfileTypes(values);
            }}
            maxCount={1}
            className="!w-fit !min-w-[280px]"
          />
          <MultiSelect
            options={CHECK_IN_STATUS_OPTIONS}
            defaultValue={statuses}
            placeholder="Check-in Status"
            onValueChange={(values) => {
              setStatuses(values);
            }}
            maxCount={1}
            className="!w-fit !min-w-[280px]"
          />
          <MultiSelect
            placeholder="Room"
            defaultValue={rooms}
            onValueChange={(values) => {
              setRooms(values);
            }}
            searchCallback={async (searchValue) => {
              const deviceSearchValue = {
                ...searchValue,
                device_type: [
                  DeviceType.OfficeCheckIn,
                  DeviceType.MeetingRoom,
                  DeviceType.ProjectRoom,
                ],
              };
              return mapSerialNumberResponseToDropdownSearchResponse(
                await IpadConfigService.GetIpadSerialNumbers(deviceSearchValue),
              );
            }}
            maxCount={1}
            className="!w-fit !min-w-[300px]"
          />
          <DateRangePicker
            ref={dateRangePickerRef}
            allowClear
            hasSuffixIcon
            required
            initialDateFrom={timestamp.from}
            initialDateTo={timestamp.to}
            onUpdate={({ range }) => setTimestamp(range)}
            align="start"
            locale="en-GB"
            buttonClassName="!w-[330px] sm:!w-[280px] font-medium text-sm !h-11"
            placeholder="Timestamp"
            startMonth={minSelectableDate}
            endMonth={new Date()}
            calendarDisabled={{
              before: minSelectableDate,
              after: new Date(),
            }} // Disable dates outside of the allowed range
            infoMessage={`Please select a date within the past ${maxPastDays} days from today`}
          />
          {isFiltered && (
            <Button
              variant="link"
              prefixIcon={<CloseIcon className="w-5 h-5" />}
              onClick={onClearFilters}
              className="!pl-1 py-3"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
      <div className="w-full relative">
        {isEmpty && !isLoading && !isFetching ? (
          <NotFoundResult />
        ) : (
          <>
            <Table className="mt-4" size="lg">
              <TableHeader>
                <TableRow>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'timestamp' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('timestamp', direction)}
                  >
                    Timestamp
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'profileId' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('profileId', direction)}
                  >
                    Face Profile ID
                  </TableHead>
                  <TableHead
                    sortable
                    className="w-[30%]"
                    sortDirection={sortBy === 'fullName' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('fullName', direction)}
                  >
                    Full Name
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === 'profileType' ? (isDescending ? 'desc' : 'asc') : null
                    }
                    onSort={(direction) => handleSort('profileType', direction)}
                  >
                    Profile Type
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'employeeId' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('employeeId', direction)}
                  >
                    Employee ID
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'deviceName' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('deviceName', direction)}
                    className="w-[25%]"
                  >
                    Room
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'status' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('status', direction)}
                  >
                    Check-in Status
                  </TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {checkInLogs.map((checkInLog) => (
                  <TableRow key={checkInLog.id}>
                    <TableCell className="font-medium underline text-brand-primary-base">
                      <Link
                        to={generatePath(AppRoutes.CHECK_IN_LOG_DETAILS, { id: checkInLog.id })}
                      >
                        {formatDate({
                          date: new Date(checkInLog.timestamp),
                          hasTime: true,
                        })}
                      </Link>
                    </TableCell>
                    <TableCell className="font-medium">
                      {checkInLog.profileId ? (
                        checkInLog.profileId
                      ) : (
                        <span className="tracking-[1px]">--</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium w-[30%] max-w-[458px]">
                      <span className="block truncate" title={checkInLog.fullName}>
                        {checkInLog.fullName ? (
                          checkInLog.fullName
                        ) : (
                          <span className="tracking-[1px]">--</span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">
                      {getProfileTypeLabel(checkInLog.profileType)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {checkInLog.employeeId ? (
                        checkInLog.employeeId
                      ) : (
                        <span className="tracking-[1px]">--</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium w-[25%] max-w-[240px]">
                      <span
                        className="block truncate"
                        title={checkInLog.device ? checkInLog.device.name : '--'}
                      >
                        {checkInLog.device ? (
                          checkInLog.device.name
                        ) : (
                          <span className="tracking-[1px]">--</span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell
                      cellType="status"
                      statusVariant={getCheckInStatusVariant(checkInLog.status)}
                      statusIcon={(() => {
                        const Icon = getCheckInStatusIcon(checkInLog.status);
                        return <Icon />;
                      })()}
                    >
                      {getCheckInStatusLabel(checkInLog.status)}
                    </TableCell>
                    <TableCell align="right" cellType="custom" className="!p-0">
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          className="w-[63px] aspect-square"
                          asChild
                        ></DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="start"
                          side="left"
                          alignOffset={26}
                          sideOffset={-16}
                        >
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                generatePath(AppRoutes.CHECK_IN_LOG_DETAILS, { id: checkInLog.id }),
                              )
                            }
                          >
                            View Details
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </>
        )}
        {(isLoading || isFetching) && <Loading fullScreen />}
      </div>
    </div>
  );
};
export default CheckInLogList;
